import React, { useState } from 'react';
import MediaManagement from './MediaManagement';
import { PermMedia as MediaIcon } from '@mui/icons-material';
import { Routes, Route, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  AccountBalanceWallet as WalletIcon,
  NotificationsActive as NotificationsActiveIcon,
  Receipt as TransactionIcon,
  Chat as ChatIcon,
  Email as EmailIcon,
  Campaign as CampaignIcon,
  VerifiedUser as VerifiedUserIcon,
  Notifications as NotificationsIcon,
  AccountBalance as BalanceIcon,
  Settings as SettingsIcon,
  History as HistoryIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Wifi as WifiIcon,
  Group as GroupIcon,
  SmartToy as SmartNotificationsIcon,
  Business as BusinessIcon,
  Event as EventIcon,
  AccountBalanceWallet as WithdrawalIcon,
  Share as ReferralIcon,
  CreditCard as CreditCardIcon,
  Savings as SavingsIcon,
  CardGiftcard as GiftIcon,
  TrendingUp as EarningsIcon,
  Handshake as HandshakeIcon,
  Palette as PaletteIcon,
  Warning as WarningIcon,
  Terminal as TerminalIcon,
  DeleteSweep as DeleteSweepIcon,
} from '@mui/icons-material';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import Overview from '../components/Overview';
import AllUsers from './allUsers';
import AllTransactions from './allTransactions';
import Settings from './Settings';
import PushNotifications from './PushNotifications';
import Notifications from './Notifications';
import Newsletter from './Newsletter';
import Adverts from './Adverts';
import KYCVerification from './KYCVerification';
import SystemBalance from './SystemBalance';
import Activities from './Activities';
import AllAdmins from './SuperAdmin';
import OnlineUsers from './OnlineUsers';
import BeneficiaryGroups from './BeneficiaryGroups';
import SmartNotifications from './SmartNotifications';
import ChatPage from './ChatPage';
import VendorApplications from './VendorApplications';
import EventManagement from './EventManagement';
import EventDesignTemplates from './EventDesignTemplates';
import WithdrawalRequests from './WithdrawalRequests';
import ReferralManagement from './ReferralManagement';
import AffiliateManagement from './AffiliateManagement';
import AtmCardManagement from './AtmCardManagement';
import SavingsManagement from './SavingsManagement';
import SpinManagement from './SpinManagement';
import Earnings from './Earnings';
import OnlineAdmins from './OnlineAdmins';
import ManageEventAdmin from './ManageEventAdmin';
import FundingApprovals from './FundingApprovals';
import SuspiciousBalances from './SuspiciousBalances';
import SystemLogs from './SystemLogs';
import DeletedTransactions from './DeletedTransactions';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, hasActionPermission } from '../utils/permissions';

const drawerWidth = 280;

const Dashboard = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Media Management', icon: <MediaIcon />, path: '/dashboard/media-management' },
    { text: 'Users', icon: <PeopleIcon />, path: '/dashboard/users' },
    { text: 'Online Users', icon: <WifiIcon />, path: '/dashboard/online-users' },
    { text: 'All Notifications', icon: <NotificationsActiveIcon />, path: '/dashboard/allnotifications' },
    { text: 'All Transactions', icon: <TransactionIcon />, path: '/dashboard/transactions' },
    { text: 'Deleted Transactions', icon: <DeleteSweepIcon />, path: '/dashboard/transactions/deleted' },
    { text: 'Balance', icon: <BalanceIcon />, path: '/dashboard/balance' },
    { text: 'Earnings', icon: <EarningsIcon />, path: '/dashboard/earnings' },
    { text: 'All Chats', icon: <ChatIcon />, path: '/dashboard/chats' },
    { text: 'Push Notifications', icon: <NotificationsIcon />, path: '/dashboard/notifications' },
    { text: 'Smart Notifications', icon: <SmartNotificationsIcon />, path: '/dashboard/smart-notifications' },
    { text: 'Newsletter', icon: <EmailIcon />, path: '/dashboard/newsletter' },
    { text: 'Adverts', icon: <CampaignIcon />, path: '/dashboard/adverts' },
    { text: 'KYC Verification', icon: <VerifiedUserIcon />, path: '/dashboard/kyc' },
    { text: 'Referral Management', icon: <ReferralIcon />, path: '/dashboard/referral-management' },
    { text: 'Affiliate Management', icon: <HandshakeIcon />, path: '/dashboard/affiliate-management' },
    { text: 'Activities', icon: <HistoryIcon />, path: '/dashboard/activities' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/dashboard/settings' },
    { text: 'All Admins', icon: <AdminPanelSettingsIcon />, path: '/dashboard/alladmins' },
    { text: 'Vendor Applications', icon: <BusinessIcon />, path: '/dashboard/vendor-applications' },
    { text: 'Event Management', icon: <EventIcon />, path: '/dashboard/event-management' },
    { text: 'Event Design Templates', icon: <PaletteIcon />, path: '/dashboard/event-design-templates' },
    { text: 'Withdrawal Requests', icon: <WithdrawalIcon />, path: '/dashboard/withdrawal-requests' },
    { text: 'ATM Card Management', icon: <CreditCardIcon />, path: '/dashboard/atm-cards' },
    { text: 'Savings Management', icon: <SavingsIcon />, path: '/dashboard/savings-management' },
    { text: 'Spin & Win', icon: <GiftIcon />, path: '/dashboard/spin-management' },
    { text: 'Funding Approvals', icon: <BalanceIcon />, path: '/dashboard/funding-approvals' },
    { text: 'Suspicious Balances', icon: <WarningIcon />, path: '/dashboard/suspicious-balances', badge: true, color: 'error' },
    { text: 'System Logs', icon: <TerminalIcon />, path: '/dashboard/logs' },
    ...(user && user.type === 'superadmin' ? [
      { text: 'Beneficiary Groups', icon: <GroupIcon />, path: '/dashboard/beneficiary-groups' },
      { text: 'Online Admins', icon: <WifiIcon />, path: '/dashboard/online-admins' },
    ] : [])
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    // Handle logout
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Header onLogout={handleLogout} onMenuClick={handleDrawerToggle} />
      <Sidebar
        menuItems={menuItems}
        onNavigate={handleNavigate}
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 4 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: '56px', sm: '64px' },
          position: 'relative',
          zIndex: 1,
          minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
          bgcolor: theme.palette.background.default,
          transition: 'all 0.3s ease',
        }}
      >
        <Routes>
          <Route path="/" element={<Overview />} />
          {hasPermission('Users', user?.permissions || [], user?.type) && (
            <Route path="/users" element={<AllUsers />} />
          )}
          {hasPermission('Online Users', user?.permissions || [], user?.type) && (
            <Route path="/online-users" element={<OnlineUsers />} />
          )}
          {hasPermission('All Transactions', user?.permissions || [], user?.type) && (
            <Route path="/transactions" element={<AllTransactions />} />
          )}
          {hasPermission('Deleted Transactions', user?.permissions || [], user?.type) && (
            <Route path="/transactions/deleted" element={<DeletedTransactions />} />
          )}
          {hasPermission('Balance', user?.permissions || [], user?.type) && (
            <Route path="/balance" element={<SystemBalance />} />
          )}
          {hasActionPermission('view_financial_data', user?.permissions || [], user?.type) && (
            <Route path="/earnings" element={<Earnings />} />
          )}
          {hasPermission('All Chats', user?.permissions || [], user?.type) && (
            <Route path="/chats" element={<ChatPage />} />
          )}
          {hasPermission('All Notifications', user?.permissions || [], user?.type) && (
            <Route path="/allnotifications" element={<Notifications />} />
          )}
          {hasPermission('Push Notifications', user?.permissions || [], user?.type) && (
            <Route path="/notifications" element={<PushNotifications />} />
          )}
          {hasPermission('Smart Notifications', user?.permissions || [], user?.type) && (
            <Route path="/smart-notifications" element={<SmartNotifications />} />
          )}
          {hasPermission('Newsletter', user?.permissions || [], user?.type) && (
            <Route path="/newsletter" element={<Newsletter />} />
          )}
          {hasPermission('Adverts', user?.permissions || [], user?.type) && (
            <Route path="/adverts" element={<Adverts />} />
          )}
          {hasPermission('KYC Verification', user?.permissions || [], user?.type) && (
            <Route path="/kyc" element={<KYCVerification />} />
          )}
          {hasPermission('Activities', user?.permissions || [], user?.type) && (
            <Route path="/activities" element={<Activities />} />
          )}
          {hasPermission('Settings', user?.permissions || [], user?.type) && (
            <Route path="/settings" element={<Settings />} />
          )}
          {hasPermission('All Admins', user?.permissions || [], user?.type) && (
            <Route path="/alladmins" element={<AllAdmins />} />
          )}
          {hasPermission('Vendor Applications', user?.permissions || [], user?.type) && (
            <Route path="/vendor-applications" element={<VendorApplications />} />
          )}
          {hasPermission('Event Management', user?.permissions || [], user?.type) && (
            <Route path="/event-management" element={<EventManagement />} />
          )}
          {hasPermission('Event Management', user?.permissions || [], user?.type) && (
            <Route path="/event-management/:eventId" element={<ManageEventAdmin />} />
          )}
          {hasPermission('Event Management', user?.permissions || [], user?.type) && (
            <Route path="/event-design-templates" element={<EventDesignTemplates />} />
          )}
          {hasPermission('Withdrawal Requests', user?.permissions || [], user?.type) && (
            <Route path="/withdrawal-requests" element={<WithdrawalRequests />} />
          )}
          {hasPermission('Referral Management', user?.permissions || [], user?.type) && (
            <Route path="/referral-management" element={<ReferralManagement />} />
          )}
          {hasPermission('Affiliate Management', user?.permissions || [], user?.type) && (
            <Route path="/affiliate-management" element={<AffiliateManagement />} />
          )}
          {hasPermission('ATM Card Management', user?.permissions || [], user?.type) && (
            <Route path="/atm-cards" element={<AtmCardManagement />} />
          )}
          {hasPermission('Savings Management', user?.permissions || [], user?.type) && (
            <Route path="/savings-management" element={<SavingsManagement />} />
          )}
          {hasPermission('Spin & Win', user?.permissions || [], user?.type) && (
            <Route path="/spin-management" element={<SpinManagement />} />
          )}
          {hasPermission('Beneficiary Groups', user?.permissions || [], user?.type) && (
            <Route path="/beneficiary-groups" element={<BeneficiaryGroups />} />
          )}
          {(user?.type === 'superadmin') && (
            <Route path="/online-admins" element={<OnlineAdmins />} />
          )}
          {hasPermission('Funding Approvals', user?.permissions || [], user?.type) && (
            <Route path="/funding-approvals" element={<FundingApprovals />} />
          )}
          {hasPermission('Media Management', user?.permissions || [], user?.type) && (
            <Route path="/media-management" element={<MediaManagement />} />
          )}
          {hasPermission('Suspicious Balances', user?.permissions || [], user?.type) && (
            <Route path="/suspicious-balances" element={<SuspiciousBalances />} />
          )}
          {hasPermission('System Logs', user?.permissions || [], user?.type) && (
            <Route path="/logs" element={<SystemLogs />} />
          )}
        </Routes>
      </Box>
    </Box>
  );
};

export default Dashboard; 